import React, { useState } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Paper,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

/**
 * Componente de filtro temporal para el dashboard
 * Permite seleccionar periodo predefinido o rango personalizado
 */
const FiltroTemporal = ({ periodo, onPeriodoChange, fechaInicio, fechaFin, onFechasChange }) => {
  const [localPeriodo, setLocalPeriodo] = useState(periodo || 'semana');
  const [localFechaInicio, setLocalFechaInicio] = useState(fechaInicio || null);
  const [localFechaFin, setLocalFechaFin] = useState(fechaFin || null);

  const handlePeriodoChange = (event) => {
    const newPeriodo = event.target.value;
    setLocalPeriodo(newPeriodo);

    if (newPeriodo !== 'personalizado') {
      setLocalFechaInicio(null);
      setLocalFechaFin(null);
      onPeriodoChange(newPeriodo, null, null);
    } else {
      onPeriodoChange(newPeriodo, localFechaInicio, localFechaFin);
    }
  };

  const handleFechaInicioChange = (newValue) => {
    setLocalFechaInicio(newValue);
    if (newValue && localFechaFin) {
      onFechasChange(newValue, localFechaFin);
    }
  };

  const handleFechaFinChange = (newValue) => {
    setLocalFechaFin(newValue);
    if (localFechaInicio && newValue) {
      onFechasChange(localFechaInicio, newValue);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 2,
        mb: 3
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Icono y label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayIcon sx={{ color: '#1A237E', fontSize: '1.25rem' }} />
          <Typography
            variant="subtitle2"
            sx={{
              color: '#1A237E',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
          >
            Periodo:
          </Typography>
        </Box>

        {/* Select de periodo */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={localPeriodo}
            onChange={handlePeriodoChange}
            sx={{
              bgcolor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(26, 35, 126, 0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(26, 35, 126, 0.4)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1A237E',
              }
            }}
          >
            <MenuItem value="semana">Semana Actual</MenuItem>
            <MenuItem value="mes">Mes Actual</MenuItem>
            <MenuItem value="mes_pasado">Mes Pasado</MenuItem>
            <MenuItem value="personalizado">Personalizado</MenuItem>
          </Select>
        </FormControl>

        {/* Date pickers para periodo personalizado */}
        {localPeriodo === 'personalizado' && (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <DatePicker
                label="Fecha Inicio"
                value={localFechaInicio}
                onChange={handleFechaInicioChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      bgcolor: 'white',
                      minWidth: 160,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        '& fieldset': {
                          borderColor: 'rgba(26, 35, 126, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(26, 35, 126, 0.4)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1A237E',
                        }
                      }
                    }
                  }
                }}
              />

              <Typography sx={{ color: '#999' }}>—</Typography>

              <DatePicker
                label="Fecha Fin"
                value={localFechaFin}
                onChange={handleFechaFinChange}
                minDate={localFechaInicio}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      bgcolor: 'white',
                      minWidth: 160,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        '& fieldset': {
                          borderColor: 'rgba(26, 35, 126, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(26, 35, 126, 0.4)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1A237E',
                        }
                      }
                    }
                  }
                }}
              />
            </Box>
          </LocalizationProvider>
        )}
      </Box>
    </Paper>
  );
};

export default FiltroTemporal;
